import { useState } from "react";
import { Tables } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { DateRange } from "react-day-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Loader2 } from "lucide-react";
import { COUNTRIES, countryCodeToFlag } from "@/utils/countryUtils";

type BusyPeriod = Tables<"busy_periods">;

interface EditBusyPeriodDialogProps {
  period: BusyPeriod | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditBusyPeriodDialog({ period, open, onOpenChange }: EditBusyPeriodDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    period ? { from: new Date(period.start_date), to: new Date(period.end_date) } : undefined
  );
  const [description, setDescription] = useState(period?.description || "");
  const [teamScope, setTeamScope] = useState<"sales" | "production">(
    (period?.team_scope as "sales" | "production") || "production"
  );
  const [country, setCountry] = useState(period?.country || "");

  // Reset form when period changes
  const resetForm = (p: BusyPeriod) => {
    setDateRange({ from: new Date(p.start_date), to: new Date(p.end_date) });
    setDescription(p.description);
    setTeamScope((p.team_scope as "sales" | "production") || "production");
    setCountry(p.country || "");
  };

  // Use effect-like pattern: reset when dialog opens with new period
  if (period && open && dateRange === undefined) {
    resetForm(period);
  }

  const handleSave = async () => {
    if (!period || !dateRange?.from || !dateRange?.to) return;
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("busy_periods")
        .update({
          start_date: dateRange.from.toISOString().split("T")[0],
          end_date: dateRange.to.toISOString().split("T")[0],
          description,
          team_scope: teamScope,
          country: country || null,
        })
        .eq("id", period.id);

      if (error) throw error;

      toast({ title: "Event updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["busy_periods"] });
      onOpenChange(false);
    } catch (error) {
      toast({ title: "Error updating event", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (!v) setDateRange(undefined);
      onOpenChange(v);
    }}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Team Scope</Label>
              <Select value={teamScope} onValueChange={(v: "sales" | "production") => setTeamScope(v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {countryCodeToFlag(c.code)} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Date Range</Label>
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              className="rounded-md border mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting || !dateRange?.from || !dateRange?.to}>
            {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
